import { Capacitor, registerPlugin } from '@capacitor/core';

// MUS-449 - Bluetooth (BLE) MIDI keyboard support.
//
// Presents the iOS system Bluetooth-MIDI pairing sheet (CABTMIDICentralViewController)
// so a user can connect a BLE keyboard. Everything downstream (source enumeration,
// note parsing, scoring) already works once a device is connected - see AppDelegate
// and MidiContext. This wrapper only exposes the pairing entry point.

export interface MuseFlowMidiPluginInterface {
  /** Present the iOS Bluetooth-MIDI pairing sheet. Resolves once shown. */
  presentBluetoothMidiPairing(): Promise<{ presented: boolean }>;
  /** Whether the native pairing sheet is available (iOS only). */
  isBluetoothMidiPairingAvailable(): Promise<{ available: boolean }>;
}

const MuseFlowMidi = registerPlugin<MuseFlowMidiPluginInterface>('MuseFlowMidi');

export default MuseFlowMidi;

export interface BluetoothMidiEnv {
  /** Capacitor.isNativePlatform() - true inside the native app. */
  isNative: boolean;
  /** Capacitor.getPlatform() - 'ios' | 'android' | 'web'. */
  platform: string;
  /** Capacitor.isPluginAvailable('MuseFlowMidi'). */
  pluginAvailable: boolean;
}

/**
 * Pure predicate: should the "Connect Bluetooth keyboard" affordance be offered?
 * Only on native iOS with the plugin present. The iOS BLE-MIDI pairing sheet is a
 * CoreAudioKit (iOS-only) API; on Android and web, Bluetooth pairing is done at the
 * OS level, so there is no in-app sheet to present.
 */
export const shouldOfferBluetoothMidiPairing = (env: BluetoothMidiEnv): boolean =>
  env.isNative && env.platform === 'ios' && env.pluginAvailable;

/** Live check against the Capacitor runtime. */
export const isBluetoothMidiPairingSupported = (): boolean =>
  shouldOfferBluetoothMidiPairing({
    isNative: Capacitor.isNativePlatform(),
    platform: Capacitor.getPlatform(),
    pluginAvailable: Capacitor.isPluginAvailable('MuseFlowMidi'),
  });

/**
 * Present the pairing sheet if supported. Returns whether it was shown. Never
 * throws - a rejected native call resolves to false so the UI can no-op quietly.
 */
export const presentBluetoothMidiPairing = async (): Promise<boolean> => {
  if (!isBluetoothMidiPairingSupported()) return false;
  try {
    const res = await MuseFlowMidi.presentBluetoothMidiPairing();
    return Boolean(res?.presented);
  } catch {
    return false;
  }
};
